import { useState, useEffect, useCallback } from 'react';
import { leadsAPI } from '../../lib/api';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

const STATUS_COLORS = { NEW: 'bg-blue-100 text-blue-800', ATTEMPTED: 'bg-amber-100 text-amber-800', CONTACTED: 'bg-emerald-100 text-emerald-800', NURTURING: 'bg-purple-100 text-purple-800', CLOSED: 'bg-gray-200 text-gray-700', CONVERTED: 'bg-indigo-600 text-white' };
const EMPTY_LEAD = { company: '', contact_name: '', email: '', phone: '', country: '', lead_type: 'CUSTOMER' };

export default function LeadsListTab({ caps, onOpenLead }) {
  const [view, setView] = useState('mine');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ leads: [], total: 0, pages: 1 });
  const [showAdd, setShowAdd] = useState(false);
  const [newLead, setNewLead] = useState(EMPTY_LEAD);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { view, page };
      if (status) params.status = status;
      if (q) params.q = q;
      const { data } = await leadsAPI.list(params);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load leads');
      setResult({ leads: [], total: 0, pages: 1 });
    }
  }, [view, status, q, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [qInput]);

  const submitAddLead = async (keep) => {
    if (!newLead.company || !newLead.country) { toast.error('Company and country are required'); return; }
    setAdding(true);
    try {
      await leadsAPI.create(keep ? { ...newLead, keep: true } : newLead);
      toast.success('Lead added');
      setShowAdd(false); setNewLead(EMPTY_LEAD);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to add lead';
      if (e?.response?.status === 409 && detail.includes('keep=true')) {
        if (window.confirm(`${detail.replace(' — resend with keep=true to add anyway', '')}\n\nAdd anyway?`)) {
          await submitAddLead(true);
          return;
        }
      } else {
        toast.error(detail);
      }
    } finally { setAdding(false); }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="leads-list-tab">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={view} onValueChange={(v) => { setView(v); setPage(1); }}>
            <SelectTrigger className="w-[140px] h-9" data-testid="leads-view-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mine">My leads</SelectItem>
              {caps.is_manager && <SelectItem value="team">Team</SelectItem>}
              {caps.can_admin && <SelectItem value="all">All</SelectItem>}
            </SelectContent>
          </Select>
          <Select value={status || 'ALL'} onValueChange={(v) => { setStatus(v === 'ALL' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[150px] h-9" data-testid="leads-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Any status</SelectItem>
              {['NEW', 'ATTEMPTED', 'CONTACTED', 'NURTURING', 'CLOSED', 'CONVERTED'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Search company/contact/email" value={qInput} onChange={(e) => setQInput(e.target.value)} className="w-64 h-9" data-testid="leads-search-input" />
        </div>
        {caps.can_work && (
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="leads-add-lead-btn"><Plus className="h-4 w-4 mr-1" /> Add lead</Button>
        )}
      </div>

      <Card data-testid="leads-table-card">
        {result.leads.length === 0 ? (
          <p className="text-muted-foreground p-6 text-center" data-testid="leads-empty-state">No leads found</p>
        ) : (
          <div className="divide-y">
            {result.leads.map(l => (
              <div key={l.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 cursor-pointer" onClick={() => onOpenLead(l.id)} data-testid={`lead-row-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate flex items-center gap-2">{l.company} <span className="text-xs text-muted-foreground font-normal">{l.code}</span></div>
                  <div className="text-xs text-muted-foreground truncate">{l.contact_name} · {l.country} · {l.owner_name}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-3">
                  {!!l.rating && <Badge variant="outline">★{l.rating}</Badge>}
                  <Badge className={STATUS_COLORS[l.status] || ''}>{l.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground" data-testid="leads-total-count">{result.total} lead(s)</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="leads-prev-page">Prev</Button>
          <span className="text-sm py-1.5">Page {page} / {result.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= result.pages} onClick={() => setPage(p => p + 1)} data-testid="leads-next-page">Next</Button>
        </div>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="leads-add-lead-dialog">
          <DialogHeader><DialogTitle>Add a lead</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label>Company *</Label><Input value={newLead.company} onChange={(e) => setNewLead(s => ({ ...s, company: e.target.value }))} data-testid="add-lead-company-input" /></div>
            <div><Label>Contact name</Label><Input value={newLead.contact_name} onChange={(e) => setNewLead(s => ({ ...s, contact_name: e.target.value }))} data-testid="add-lead-contact-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Email</Label><Input value={newLead.email} onChange={(e) => setNewLead(s => ({ ...s, email: e.target.value }))} data-testid="add-lead-email-input" /></div>
              <div><Label>Phone</Label><Input value={newLead.phone} onChange={(e) => setNewLead(s => ({ ...s, phone: e.target.value }))} data-testid="add-lead-phone-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Country *</Label><Input value={newLead.country} onChange={(e) => setNewLead(s => ({ ...s, country: e.target.value }))} data-testid="add-lead-country-input" /></div>
              <div>
                <Label>Lead type</Label>
                <Select value={newLead.lead_type} onValueChange={(v) => setNewLead(s => ({ ...s, lead_type: v }))}>
                  <SelectTrigger className="h-9" data-testid="add-lead-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CUSTOMER">Customer</SelectItem>
                    <SelectItem value="VENDOR">Vendor</SelectItem>
                    <SelectItem value="BOTH">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={() => submitAddLead(false)} disabled={adding} data-testid="add-lead-submit-btn" className="w-full">Add lead</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
